import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api, type CustomersStatus, type SyncResult } from '@/lib/api';

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/** Admin : état du carnet d'adresses et mise à jour depuis l'API Fiche client. */
export default function CustomersAdmin() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['customers-status'],
    queryFn: () => api<CustomersStatus>('/api/admin/customers/status'),
  });

  const sync = useMutation({
    mutationFn: () => api<SyncResult>('/api/admin/customers/sync', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers-status'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  return (
    <section className="panel space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="panel-title">Clients (API Fiche client)</h2>
          <p className="text-muted-foreground text-sm tabular-nums">
            {status.data
              ? `${status.data.active.toLocaleString('fr-CA')} clients actifs · mis à jour ${fmtDate(status.data.lastUpdate)}`
              : 'Chargement…'}
          </p>
        </div>
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending || status.data?.ficheClientConfigured === false}
          className="border-primary text-primary hover:bg-accent flex h-11 items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition-colors disabled:opacity-50"
        >
          <RefreshCw className={sync.isPending ? 'size-4 animate-spin' : 'size-4'} />
          {sync.isPending ? 'Import en cours…' : 'Mettre à jour'}
        </button>
      </div>

      {status.data?.ficheClientConfigured === false && (
        <p className="text-destructive text-sm">API Fiche client non configurée sur ce serveur.</p>
      )}
      {sync.isError && <p className="text-destructive text-sm">{(sync.error as Error).message}</p>}
      {sync.data && (
        <div className="text-sm">
          <p className="font-medium">
            {sync.data.imported.toLocaleString('fr-CA')} fiches · {sync.data.jobs.toLocaleString('fr-CA')} jobs ·{' '}
            {sync.data.suspended.toLocaleString('fr-CA')} suspendus
          </p>
        </div>
      )}
    </section>
  );
}
